import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useThemeName } from 'tamagui'

import { API_KEY as GOOGLE_MAPS_API_KEY } from '@data/config/googleMaps'

import { EventScreenMap_event$key } from './__generated__/EventScreenMap_event.graphql'

export const EventScreenMap = ({
  event,
}: {
  event: EventScreenMap_event$key
}) => {
  const themeName = useThemeName()
  const data = useFragment(
    graphql`
      fragment EventScreenMap_event on Event {
        name
        hasVenue

        venue {
          name

          address {
            addressLine1
            addressLine2
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode

            formattedAddress
          }
        }

        seoVenue {
          address {
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode

            location {
              geojson
            }
          }
        }

        myAttendance {
          status
        }
      }
    `,
    event
  )

  const seoVenueCoordinates = useMemo(() => {
    if (Array.isArray(data.seoVenue?.address?.location?.geojson?.coordinates)) {
      return [
        ...data.seoVenue?.address?.location?.geojson.coordinates,
      ].reverse()
    }
  }, [data])

  const googleMapsQuery = useMemo(() => {
    if (!data.hasVenue) {
      return null
    }

    if (
      data.venue?.address &&
      data.myAttendance?.status === 'ATTENDING_IN_PERSON'
    ) {
      if (data.venue.address.formattedAddress) {
        return `${data.venue.name}, ${data.venue.address.formattedAddress}`
      }

      const {
        addressLine1,
        addressLine2,
        city,
        stateProvinceRegionCode,
        zipPostalCode,
        countryCode,
      } = data.venue.address

      return [
        data.venue.name,
        addressLine1,
        addressLine2,
        city,
        stateProvinceRegionCode,
        zipPostalCode,
        countryCode,
      ]
        .filter(Boolean)
        .join(',')
    }

    if (data.seoVenue?.address && !seoVenueCoordinates) {
      const { city, stateProvinceRegionCode, zipPostalCode, countryCode } =
        data.seoVenue.address

      return [city, stateProvinceRegionCode, zipPostalCode, countryCode]
        .filter(Boolean)
        .join(',')
    }

    return null
  }, [data, seoVenueCoordinates])

  if (!data.hasVenue) {
    return null
  }

  const renderedURL = googleMapsQuery
    ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(
        googleMapsQuery
      )}&zoom=15`
    : `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=${seoVenueCoordinates}&zoom=15`

  const isDarkMode = themeName === 'dark'

  return (
    <iframe
      key={renderedURL}
      title={`Map for ${googleMapsQuery || data.name}`}
      height='199'
      style={{
        border: 0,
        borderRadius: 24,
        display: 'flex',
        boxShadow: isDarkMode
          ? 'rgba(0, 0, 0, 0.16) 0px 4px 8px -4px, rgba(0, 0, 0, 0.12) 0px 5px 26px -7px'
          : 'rgba(10, 37, 64, 0.04) 0px 4px 8px -4px, rgba(10, 37, 64, 0.03) 0px 5px 26px -7px',
        width: '100%',
        filter: isDarkMode
          ? 'invert(1) hue-rotate(180deg)'
          : undefined,
      }}
      loading='lazy'
      allowFullScreen
      referrerPolicy='no-referrer-when-downgrade'
      src={renderedURL}
    />
  )
}
